import { useState } from 'react'
import { creerDemandePosition } from '../api/operations'
import { encoder } from '../api/pluscode'
import { coordonneeValide } from '../api/geo'
import { lienPosition, preparerDemandePosition, VALIDITE_LIEN_HEURES, type PreparationPosition } from '../api/lienPosition'
import type { Course } from '../api/types'
import { Icone } from './Icone'
import { useDroits } from './Contexte'

/**
 * « Demander la position au destinataire » : crée la demande (la base rend le jeton UNE fois), bâtit
 * le lien `…/position?t=…` et ouvre WhatsApp avec le message prérempli — la personne relit et envoie
 * elle-même. Quand le destinataire partage sa position, la course est mise à jour en direct (temps
 * réel déjà branché sur `courses`) et, si elle est encore « créée », la base recalcule le prix.
 */
export function DemandePosition({
  course,
  telephoneClient,
  marque,
  surNotifier,
}: {
  course: Pick<Course, 'id' | 'ref' | 'statut' | 'dropoff_lat' | 'dropoff_lng' | 'recipient_name' | 'recipient_phone' | 'client_phone'>
  /** Numéro de la fiche client, en dernier recours. */
  telephoneClient: string | null
  marque: string
  surNotifier?: (message: string) => void
}) {
  const droits = useDroits()
  const [etat, setEtat] = useState<'repos' | 'creation' | 'pret' | 'erreur'>('repos')
  const [lien, setLien] = useState<string | null>(null)
  const [prep, setPrep] = useState<PreparationPosition | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [copie, setCopie] = useState(false)

  const termine = course.statut === 'livree' || course.statut === 'annulee'
  const connu = coordonneeValide(course.dropoff_lat, course.dropoff_lng)

  async function demander() {
    setEtat('creation')
    setMessage(null)
    setCopie(false)
    try {
      const jeton = await creerDemandePosition(course.id)
      const url = lienPosition(jeton)
      const p = preparerDemandePosition(course, telephoneClient, { ref: course.ref, marque, lien: url })
      setLien(url)
      setPrep(p)
      setEtat('pret')
      /* Ouverture directe de WhatsApp ; si le navigateur la bloque (appel asynchrone), le bouton ci-dessous prend le relais. */
      if (p.ok) window.open(p.url, '_blank', 'noopener,noreferrer')
      surNotifier?.(p.ok ? 'Lien prêt — WhatsApp s’ouvre avec le message' : 'Lien créé — copiez-le pour l’envoyer')
    } catch (e) {
      setEtat('erreur')
      setMessage(e instanceof Error ? e.message : String(e))
    }
  }

  async function copier() {
    if (!lien) return
    try {
      await navigator.clipboard.writeText(lien)
      setCopie(true)
    } catch {
      setMessage('Copie impossible depuis ce navigateur : sélectionnez le lien ci-dessus.')
    }
  }

  return (
    <div>
      {connu ? (
        <div className="etat-point ok">
          <Icone nom="check" /> Position de livraison connue :
          <span className="mono">
            {(course.dropoff_lat as number).toFixed(5)} ; {(course.dropoff_lng as number).toFixed(5)}
          </span>
          <span>· Plus Code {encoder(course.dropoff_lat as number, course.dropoff_lng as number)}</span>
        </div>
      ) : (
        <div className="etat-point">Pas encore de position de livraison : le livreur n’a qu’une adresse écrite.</div>
      )}

      {!termine && droits.peut('courses') && (
        <div style={{ marginTop: 8 }}>
          <button className="btn btn-green btn-sm" onClick={() => void demander()} disabled={etat === 'creation'}>
            <Icone nom="pin" /> {etat === 'creation' ? 'Création du lien…' : connu ? 'Redemander la position au destinataire' : 'Demander la position au destinataire'}
          </button>
          <div className="t-muted" style={{ marginTop: 6, fontSize: 12 }}>
            Envoie un lien personnel (valable {VALIDITE_LIEN_HEURES} h, un seul usage) : le destinataire touche « Partager ma position », rien à installer.
          </div>
        </div>
      )}

      {etat === 'pret' && lien && (
        <div className="lien-prepare" role="status">
          {prep?.ok ? (
            <a className="btn btn-green btn-sm" href={prep.url} target="_blank" rel="noopener noreferrer">
              <Icone nom="chat" /> Ouvrir WhatsApp ({prep.destinataire ? 'destinataire' : 'client'})
            </a>
          ) : (
            <span className="t-muted">{prep && !prep.ok ? prep.raison : ''}</span>
          )}
          <button className="btn btn-outline btn-sm" onClick={() => void copier()}>
            {copie ? 'Lien copié' : 'Copier le lien'}
          </button>
          <code className="mono" style={{ fontSize: 11, wordBreak: 'break-all' }}>
            {lien}
          </code>
        </div>
      )}
      {message && (
        <div className="bandeau erreur" role="alert" style={{ marginTop: 8 }}>
          {message}
        </div>
      )}
    </div>
  )
}
